import { useState } from "react";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { ColumnProps, TablePaginationConfig } from "antd/es/table";
import { Button, Dropdown, message } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { Tag } from "@components/atoms";
import { generatePath, useNavigate } from "react-router-dom";
import { Voucher, PaginatorInfo } from "../queries/types";
import { LIST_VOUCHERS } from "../queries";
import { TOGGLE_VOUCHER_STATUS } from "../mutations";
import { ROUTES } from "@/consts";
import DeleteVoucherModal from "../DeleteVoucherModal";
import TierIcon from "@/assets/icons/hexagon.svg";

interface DataType {
	key: string;
}

const vouchersColumns: ColumnProps<DataType>[] = [
	{
		title: "Name",
		dataIndex: "name",
		key: "name",
	},
	{
		title: "Tier",
		dataIndex: "tier",
		key: "tier",
		render: (tier) =>
			(
				<span className="voucherTier">
					<span className="voucherTier__icon">
						<img src={TierIcon} alt="tier" />
						<b>{tier?.index}</b>
					</span>
					{tier?.name}
				</span>
			) || <span>No Tiers</span>,
	},

	{
		title: "Quantity",
		key: "codes_count",
		render: (voucher: Voucher) => (
			<span>
				<b>{voucher.redeemed_codes_count}</b>/{voucher.codes_count}
			</span>
		),
	},
	{
		title: "Status",
		key: "is_active",
		render: (voucher: Voucher) =>
			voucher.is_active ? (
				<Tag color="green" text="Active" />
			) : (
				<Tag color="red" text="Inactive" />
			),
	},

	{
		title: "",
		dataIndex: "actions",
		key: "actions",
	},
];

const useVouchersTable = () => {
	const navigate = useNavigate();
	const {
		data: vouchersData,
		loading,
		networkStatus,
		refetch,
	} = useQuery(LIST_VOUCHERS, {
		variables: {
			first: 10,
			page: 1,
		},
		notifyOnNetworkStatusChange: true,
	});

	const [toggleVoucherStatus, { loading: isToggleVoucher }] = useMutation(
		TOGGLE_VOUCHER_STATUS,
	);

	const loadingState = loading || NetworkStatus.refetch === networkStatus;
	const vouchersList: Voucher[] = loading
		? []
		: vouchersData?.listVouchers?.data;

	const paginatorInfo: PaginatorInfo =
		!loading && vouchersData.listVouchers.paginatorInfo;

	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	const [showDeactivateModal, setShowDeactivateModal] = useState(false);
	const [selectedVoucher, setSelectedVoucher] = useState<{
		id: string | undefined;
		name: string | undefined;
	} | null>(null);

	const onPagination = ({ pageSize, current }: TablePaginationConfig) => {
		refetch({
			first: pageSize,
			page: current,
		});
	};

	const onRowSelectionChange = (selectedRowKeys: React.Key[]) => {
		const ids = selectedRowKeys.length
			? selectedRowKeys.toString().split(",")
			: [];
		setSelectedRowIds(ids);
	};

	const clearSelectedRows = () => {
		setSelectedRowIds([]);
	};

	const handleToggleVoucherStatus = async (
		id: string | undefined,
		name: string | undefined,
		isActive?: boolean,
	) => {
		if (!showDeactivateModal && isActive) {
			setShowDeactivateModal(true);
			setSelectedVoucher({
				id,
				name,
			});
			return;
		}
		toggleVoucherStatus({
			variables: {
				ids: [id],
			},
			update: (cache) => {
				cache.evict({ fieldName: "listVouchers" });
			},
		})
			.then(() => {
				message.success("Voucher status updated successfully");
				setShowDeactivateModal(false);
				setSelectedVoucher(null);
				refetch();
			})
			.catch((err) => {
				if (err.graphQLErrors[0].statusCode === 422) {
					const { errors } = err.graphQLErrors[0];
					message.error(Object.values(errors)[0] as string);
					return;
				}
				message.error(err?.message || "Something went wrong");
			});
	};

	const renderActionsOnRow = (id: string, isActive: boolean, name: string) => [
		{
			label: <Button type="ghost">Edit</Button>,
			key: "edit",
			onClick: () => navigate(generatePath(ROUTES.EditVoucher, { id })),
		},
		{
			label: (
				<Button type="ghost">{isActive ? "Deactivate" : "Activate"}</Button>
			),
			key: "status",
			onClick: () => handleToggleVoucherStatus(id, name, isActive),
		},
		{
			label: <DeleteVoucherModal ids={[id]} buttonType="link" />,
			key: "delete",
			danger: true,
		},
	];

	const rows = vouchersList.map((voucher) => ({
		key: voucher.id,
		...voucher,
		actions: (
			<Dropdown
				menu={{
					items: renderActionsOnRow(
						voucher.id,
						voucher.is_active,
						voucher.name,
					),
				}}
			>
				<MoreOutlined />
			</Dropdown>
		),
	}));

	return {
		vouchersColumns,
		loading: loadingState || isToggleVoucher,
		rows,
		paginatorInfo,
		onPagination,
		onRowSelectionChange,
		clearSelectedRows,
		selectedRowIds,
		refetch,
		showDeactivateModal,
		setShowDeactivateModal,
		selectedVoucher,
		handleToggleVoucherStatus,
		isToggleVoucher,
	};
};

export default useVouchersTable;
